import { AUDIO } from '../config/tuning';

/**
 * Persistence (SPEC §3.15).
 *
 * One versioned key with a migration path, exactly as the spec lays out. The version number is
 * load-bearing: settings written by an older build must not be silently reinterpreted by a newer
 * one, because the failure mode is a player whose controls stop working and who has no idea why.
 * So an unrecognised or unmigratable save is *replaced*, not patched, and the loss is a fresh
 * settings screen rather than a corrupt one.
 *
 * Everything here is defensive about `localStorage` itself: it throws in private browsing modes,
 * it can be full, and it can contain anything at all. None of that is allowed to stop the game
 * from starting.
 */

export const SAVE_KEY = 'vz.save.v1';
export const SAVE_VERSION = 1;

export interface Settings {
  // Rendering
  resolutionScale: number;
  postEffects: Record<string, boolean>;
  drawDistanceScale: number;
  particleDensity: number;

  // Audio
  masterVolume: number;
  effectsVolume: number;
  rwrVolume: number;

  // Gameplay
  difficulty: 'training' | 'standard' | 'realistic';
  units: 'metric' | 'imperial';
  fieldOfViewDeg: number;

  // Accessibility (SPEC §3.14)
  reduceCameraShake: boolean;
  disableChromaticAberration: boolean;
  disableGrain: boolean;
  highContrastHud: boolean;
  hudScale: number;
  rwrVisualOnly: boolean;
  subtitles: boolean;
  holdToToggle: boolean;
  fullAssist: boolean;
  reduceRollingShutter: boolean;
}

export interface CampaignRecord {
  rank: string;
  score: number;
  best: number;
}

export interface SaveData {
  version: number;
  profile: { name: string; createdAtMs: number };
  settings: Settings;
  bindings: Record<string, string>;
  campaign: Record<string, CampaignRecord>;
  unlocks: string[];
  stats: Record<string, number>;
}

/**
 * The defaults, which are also the accessibility baseline.
 *
 * Every accessibility toggle defaults *off* except subtitles. Subtitles are on because SPEC §3.14
 * requires every radio call to be readable without audio and the game may never get an audio
 * context at all — a default that depends on the browser's autoplay policy is not a default.
 */
export function defaultSettings(): Settings {
  return {
    resolutionScale: 1,
    // Keys match `PostSettings` exactly, so a stored setting maps onto the pipeline without a
    // translation table — a rename on one side would otherwise silently stop applying.
    postEffects: {
      distortion: true,
      chromatic: true,
      exposure: true,
      grade: true,
      haze: true,
      blocking: true,
      jitter: true,
      rollingShutter: true,
      grain: true,
      vignette: true,
      fxaa: true,
    },
    drawDistanceScale: 1,
    particleDensity: 1,

    masterVolume: AUDIO.masterGain,
    effectsVolume: AUDIO.effectsGain,
    rwrVolume: AUDIO.rwrGain,

    difficulty: 'standard',
    units: 'metric',
    fieldOfViewDeg: 78,

    reduceCameraShake: false,
    disableChromaticAberration: false,
    disableGrain: false,
    highContrastHud: false,
    hudScale: 1,
    rwrVisualOnly: false,
    subtitles: true,
    holdToToggle: false,
    fullAssist: false,
    reduceRollingShutter: false,
  };
}

export function defaultSave(): SaveData {
  return {
    version: SAVE_VERSION,
    profile: { name: 'PILOT', createdAtMs: 0 },
    settings: defaultSettings(),
    bindings: {},
    campaign: {},
    unlocks: [],
    stats: {},
  };
}

/** Minimal storage surface, so tests can supply a map instead of a browser. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class Save {
  private data: SaveData;
  private readonly storage: StorageLike | null;

  constructor(storage: StorageLike | null = safeLocalStorage()) {
    this.storage = storage;
    this.data = this.read();
  }

  get settings(): Settings {
    return this.data.settings;
  }

  get bindings(): Record<string, string> {
    return this.data.bindings;
  }

  get campaign(): Record<string, CampaignRecord> {
    return this.data.campaign;
  }

  get unlocks(): readonly string[] {
    return this.data.unlocks;
  }

  get stats(): Record<string, number> {
    return this.data.stats;
  }

  /**
   * Record a mission result, keeping the best score.
   *
   * Best is kept rather than latest so a player can replay a mission to learn without the risk of
   * ruining a good rank — which is the difference between a campaign that invites practice and one
   * that punishes it.
   */
  recordMission(missionId: string, rank: string, score: number): void {
    const existing = this.data.campaign[missionId];
    const best = Math.max(existing?.best ?? 0, score);
    this.data.campaign[missionId] = { rank: best === score ? rank : (existing?.rank ?? rank), score, best };
    this.save();
  }

  bump(stat: string, by = 1): void {
    this.data.stats[stat] = (this.data.stats[stat] ?? 0) + by;
  }

  unlock(id: string): void {
    if (!this.data.unlocks.includes(id)) {
      this.data.unlocks.push(id);
      this.save();
    }
  }

  setBinding(action: string, code: string): void {
    this.data.bindings[action] = code;
    this.save();
  }

  clearBindings(): void {
    this.data.bindings = {};
    this.save();
  }

  /** Persist. Silently tolerates a storage that refuses to be written. */
  save(): void {
    if (this.storage === null) return;
    try {
      this.storage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch {
      // Quota exceeded, or private browsing. A game that cannot save is still a game.
    }
  }

  reset(): void {
    this.data = defaultSave();
    this.save();
  }

  private read(): SaveData {
    if (this.storage === null) return defaultSave();
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(SAVE_KEY);
    } catch {
      return defaultSave();
    }
    if (raw === null) return defaultSave();

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return defaultSave();
    }
    return migrate(parsed);
  }
}

/**
 * Bring a stored save up to the current version.
 *
 * Written as an explicit chain rather than a merge so that each version step is a place to put
 * real migration logic later. Right now there is only v1, so the job is validation: anything whose
 * shape does not survive inspection is replaced by defaults rather than half-trusted.
 */
export function migrate(parsed: unknown): SaveData {
  if (typeof parsed !== 'object' || parsed === null) return defaultSave();
  const raw = parsed as Partial<SaveData>;
  if (typeof raw.version !== 'number' || raw.version > SAVE_VERSION) return defaultSave();

  const base = defaultSave();
  return {
    version: SAVE_VERSION,
    profile: {
      name: typeof raw.profile?.name === 'string' ? raw.profile.name : base.profile.name,
      createdAtMs:
        typeof raw.profile?.createdAtMs === 'number' ? raw.profile.createdAtMs : base.profile.createdAtMs,
    },
    // Settings merge field by field: a save written before a setting existed must gain its
    // default rather than leaving the field undefined, which would read as `false` everywhere.
    settings: mergeSettings(base.settings, raw.settings),
    bindings: isStringMap(raw.bindings) ? raw.bindings : {},
    campaign: isCampaign(raw.campaign) ? raw.campaign : {},
    unlocks: Array.isArray(raw.unlocks) ? raw.unlocks.filter((u) => typeof u === 'string') : [],
    stats: isNumberMap(raw.stats) ? raw.stats : {},
  };
}

function mergeSettings(base: Settings, stored: unknown): Settings {
  if (typeof stored !== 'object' || stored === null) return base;
  const raw = stored as Record<string, unknown>;
  const out: Settings = { ...base };
  for (const key of Object.keys(base) as (keyof Settings)[]) {
    const value = raw[key];
    if (value === undefined) continue;
    const expected = typeof base[key];
    if (key === 'postEffects') {
      if (typeof value === 'object' && value !== null) {
        out.postEffects = { ...base.postEffects };
        for (const effect of Object.keys(base.postEffects)) {
          const flag = (value as Record<string, unknown>)[effect];
          if (typeof flag === 'boolean') out.postEffects[effect] = flag;
        }
      }
      continue;
    }
    if (typeof value === expected) {
      // Safe: the key's type was just checked against the default's type.
      (out as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

function isStringMap(value: unknown): value is Record<string, string> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => typeof v === 'string');
}

function isNumberMap(value: unknown): value is Record<string, number> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every((v) => typeof v === 'number');
}

function isCampaign(value: unknown): value is Record<string, CampaignRecord> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every(
    (v) =>
      typeof v === 'object' &&
      v !== null &&
      typeof (v as CampaignRecord).rank === 'string' &&
      typeof (v as CampaignRecord).score === 'number',
  );
}

/** `localStorage` throws outright in some privacy modes, so even reaching it is guarded. */
function safeLocalStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = '__vz_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}
